import MapScale from '@/Enums/Map/MapScale.enum';
import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import SimplifiReportRequest from '@/Enums/ReportingDashboard/ReportRequest/SimplifiReportRequest.enum';
import ReportView from '@/Enums/ReportingDashboard/ReportView/ReportView.enum';
import SimplifiReportType from '@/Enums/ReportingDashboard/Simplifi/ReportType.enum';
import type { IGeoJson } from '@/Interfaces/Geo/GeoJson.interface';
import type {
  IBaseDataReport,
  ICampaignReport,
  IDateReport,
  ITotalReport,
} from '@/Interfaces/ReportingDashboard/Simplifi/Report.interface';
import type {
  IAdvertiserReportRecord,
  IBaseReportRecord,
  IChannelReportRecord,
  ICityReportRecord,
  ICongressionalDistrictsReportRecord,
  ICountyReportRecord,
  ICreativeReportRecord,
  IDayOfWeekReportRecord,
  IDaypartReportRecord,
  IDealReportRecord,
  IDeviceReportRecord,
  IDmaReportRecord,
  IGeoFenceAttributionReportRecord,
  IGeoFenceReportRecord,
  IKeywordReportRecord,
  IPlacementReportRecord,
  IPublisherReportRecord,
  IStateReportRecord,
  IZipReportRecord,
} from '@/Interfaces/ReportingDashboard/Simplifi/ReportRecord.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Simplifi/State.interface';
import { GEO_BOUNDARIES, GEO_JOINT_BOUNDARIES, ZIPCODE_ENDPOINT } from '@/constants/endpoints';
import { MAP_SCALES, borderIOHeaders } from '@/constants/geo';
import { SIMPLIFI_ZIP4_SCALE, defaultTotals } from '@/constants/reportingDashboard/simplifi';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { EMPTY_FEATURE_COLLECTION, isPostableBoundariesBody } from '@/helpers/boundaries';
import { getBoundariesRequestBody, getCongressionalBoundariesRequestBody } from '@/helpers/geo';
import { simplifiReportRequestHandler } from '@/helpers/reportingDashboard/simplifi';
import type { RootState } from '@/store';
import type { SimplifiReportView } from '@/types/ReportingDashboard/Simplifi.type';
import type { RequestMap } from '@/types/RequestMap.types';
import { AsyncThunk, AsyncThunkOptions, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

enum ActionPrefix {
  Totals = 'reportingDashboardSimplifi/getTotalsReport',

  DateReport = 'reportingDashboardSimplifi/getDateReport',
  ChannelReport = 'reportingDashboardSimplifi/getChannelReport',
  DeviceReport = 'reportingDashboardSimplifi/getDeviceReport',
  PublisherReport = 'reportingDashboardSimplifi/getPublisherReport',
  CampaignReport = 'reportingDashboardSimplifi/getCampaignReport',
  CreativeReport = 'reportingDashboardSimplifi/getCreativeReport',
  AdvertiserReport = 'reportingDashboardSimplifi/getAdvertiserReport',
  DayOfWeekReport = 'reportingDashboardSimplifi/getDayOfWeekReport',
  DaypartReport = 'reportingDashboardSimplifi/getDaypartReport',
  PlacementReport = 'reportingDashboardSimplifi/getPlacementReport',
  DealReport = 'reportingDashboardSimplifi/getDealReport',
  GeoFenceReport = 'reportingDashboardSimplifi/getGeoFenceReport',
  GeoFenceAttributionReport = 'reportingDashboardSimplifi/getGeoFenceAttributionReport',
  KeywordReport = 'reportingDashboardSimplifi/getKeywordReport',

  StateReport = 'reportingDashboardSimplifi/getStateReport',
  DmaReport = 'reportingDashboardSimplifi/getDmaReport',
  CountyReport = 'reportingDashboardSimplifi/getCountyReport',
  ZipReport = 'reportingDashboardSimplifi/getZipReport',
  Zip4Report = 'reportingDashboardSimplifi/getZip4Report',
  CongressionalReport = 'reportingDashboardSimplifi/getCongressionalReport',
  CityReport = 'reportingDashboardSimplifi/getCityReport',

  ZipBoundaries = 'reportingDashboardSimplifi/getZipBoundaries',
  DmaBoundaries = 'reportingDashboardSimplifi/getDmaBoundaries',
  StateBoundaries = 'reportingDashboardSimplifi/getStateBoundaries',
  CountyBoundaries = 'reportingDashboardSimplifi/getCountyBoundaries',
  CongressionalDistrictBoundaries = 'reportingDashboardSimplifi/getCongressionalDistrictBoundaries',
  CityBoundaries = 'reportingDashboardSimplifi/getCityBoundaries',
}

const shouldFetchExplicitReport = (report: IBaseReportRecord[], requestId: string | null) => {
  if (requestId === DEFAULT_LOADING_REQUEST_ID) {
    return true;
  }
  return !report?.length && !requestId;
};

const getExplicitReportThunkOptions = (
  dataField: keyof IState,
  requestIdField?: keyof IState,
): AsyncThunkOptions<IAbortSignalThunkArg> => ({
  condition: (_, { getState }) => {
    const requestIdKey = requestIdField ?? (`${dataField}RequestId` as keyof IState);
    const {
      reportingDashboardSimplifi: { [dataField]: data, [requestIdKey]: requestId },
    } = getState() as RootState;

    return shouldFetchExplicitReport(data as IBaseReportRecord[], requestId as string);
  },
});

//#region reports
export const getTotalReport = createAsyncThunk<ITotalReport, IAbortSignalThunkArg>(
  ActionPrefix.Totals,
  simplifiReportRequestHandler(SimplifiReportType.Total, { totals: defaultTotals }),
);

export const getDateReport = createAsyncThunk<IDateReport, IAbortSignalThunkArg>(
  ActionPrefix.DateReport,
  simplifiReportRequestHandler(SimplifiReportType.Date),
  getExplicitReportThunkOptions('dateReport'),
);

export const getChannelReport = createAsyncThunk<IBaseDataReport<IChannelReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.ChannelReport,
  simplifiReportRequestHandler(SimplifiReportType.Channel),
  getExplicitReportThunkOptions('channelReport'),
);

export const getDeviceReport = createAsyncThunk<IBaseDataReport<IDeviceReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DeviceReport,
  simplifiReportRequestHandler(SimplifiReportType.Device),
  getExplicitReportThunkOptions('deviceReport'),
);

export const getPublisherReport = createAsyncThunk<IBaseDataReport<IPublisherReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.PublisherReport,
  simplifiReportRequestHandler(SimplifiReportType.Publisher),
  getExplicitReportThunkOptions('publisherReport'),
);

export const getCampaignReport = createAsyncThunk<ICampaignReport, IAbortSignalThunkArg>(
  ActionPrefix.CampaignReport,
  simplifiReportRequestHandler(SimplifiReportType.Campaign),
  getExplicitReportThunkOptions('campaignReport'),
);

export const getCreativeReport = createAsyncThunk<IBaseDataReport<ICreativeReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.CreativeReport,
  simplifiReportRequestHandler(SimplifiReportType.Creative),
  getExplicitReportThunkOptions('creativeReport'),
);

export const getAdvertiserReport = createAsyncThunk<IBaseDataReport<IAdvertiserReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.AdvertiserReport,
  simplifiReportRequestHandler(SimplifiReportType.Advertiser),
  getExplicitReportThunkOptions('advertiserReport'),
);

export const getDayOfWeekReport = createAsyncThunk<IBaseDataReport<IDayOfWeekReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DayOfWeekReport,
  simplifiReportRequestHandler(SimplifiReportType.DayOfWeek),
  getExplicitReportThunkOptions('dayOfWeekReport'),
);

export const getDaypartReport = createAsyncThunk<IBaseDataReport<IDaypartReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DaypartReport,
  simplifiReportRequestHandler(SimplifiReportType.Daypart),
  getExplicitReportThunkOptions('daypartReport'),
);

export const getPlacementReport = createAsyncThunk<IBaseDataReport<IPlacementReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.PlacementReport,
  simplifiReportRequestHandler(SimplifiReportType.Placement),
  getExplicitReportThunkOptions('placementReport'),
);

export const getDealReport = createAsyncThunk<IBaseDataReport<IDealReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DealReport,
  simplifiReportRequestHandler(SimplifiReportType.Deal),
  getExplicitReportThunkOptions('dealReport'),
);

export const getGeoFenceReport = createAsyncThunk<IBaseDataReport<IGeoFenceReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.GeoFenceReport,
  simplifiReportRequestHandler(SimplifiReportType.GeoFence, { report: [] }),
  getExplicitReportThunkOptions('geoFenceReport'),
);

// GeoFence Attribution view — companion to getGeoFenceReport. Hits a
// different Simplifi template (Geo-Conversion Lift, 87451) and dimensions
// per campaign rather than per fence; the BE service handles all that
// upstream so the FE thunk is a straight pass-through.
export const getGeoFenceAttributionReport = createAsyncThunk<
  IBaseDataReport<IGeoFenceAttributionReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.GeoFenceAttributionReport,
  simplifiReportRequestHandler(SimplifiReportType.GeoFenceAttribution, { report: [] }),
  getExplicitReportThunkOptions('geoFenceAttributionReport'),
);

export const getKeywordReport = createAsyncThunk<IBaseDataReport<IKeywordReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.KeywordReport,
  simplifiReportRequestHandler(SimplifiReportType.Keyword),
  getExplicitReportThunkOptions('keywordReport'),
);

//#region geo
export const getStateReport = createAsyncThunk<IBaseDataReport<IStateReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.StateReport,
  simplifiReportRequestHandler(SimplifiReportType.GeoState, { report: [] }),
  getExplicitReportThunkOptions('stateReport'),
);

export const getDmaReport = createAsyncThunk<IBaseDataReport<IDmaReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DmaReport,
  simplifiReportRequestHandler(SimplifiReportType.GeoDma, { report: [] }),
  getExplicitReportThunkOptions('dmaReport'),
);

export const getCountyReport = createAsyncThunk<IBaseDataReport<ICountyReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.CountyReport,
  simplifiReportRequestHandler(SimplifiReportType.GeoCounty, { report: [] }),
  getExplicitReportThunkOptions('countyReport'),
);

export const getZipReport = createAsyncThunk<IBaseDataReport<IZipReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.ZipReport,
  simplifiReportRequestHandler(SimplifiReportType.GeoZip, { report: [] }),
  getExplicitReportThunkOptions('zipReport'),
);

// Zip+4 reuses the zip row shape — a geo label + the standard delivery metrics.
export const getZip4Report = createAsyncThunk<IBaseDataReport<IZipReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.Zip4Report,
  simplifiReportRequestHandler(SimplifiReportType.GeoZip4, { report: [] }),
  getExplicitReportThunkOptions('zip4Report'),
);

export const getCongressionalReport = createAsyncThunk<
  IBaseDataReport<ICongressionalDistrictsReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.CongressionalReport,
  simplifiReportRequestHandler(SimplifiReportType.GeoCongressionalDistrict, { report: [] }),
  getExplicitReportThunkOptions('congressionalDistrictReport'),
);

export const getCityReport = createAsyncThunk<IBaseDataReport<ICityReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.CityReport,
  simplifiReportRequestHandler(SimplifiReportType.GeoCity, { report: [] }),
  getExplicitReportThunkOptions('cityReport'),
);
//#endregion

//#region dispatch map
type ActionSet = { action: AsyncThunk<any, any, any>; reportRequest: SimplifiReportRequest };

export const mapGraphDispatch: Record<MapScale, ActionSet> = {
  [MapScale.Dma]: { action: getDmaReport, reportRequest: SimplifiReportRequest.GeoDma },
  [MapScale.County]: { action: getCountyReport, reportRequest: SimplifiReportRequest.GeoCounty },
  [MapScale.Zip]: { action: getZipReport, reportRequest: SimplifiReportRequest.GeoZip },
  [MapScale.CongressionalDistricts]: {
    action: getCongressionalReport,
    reportRequest: SimplifiReportRequest.GeoCongressionalDistrict,
  },
  [MapScale.City]: { action: getCityReport, reportRequest: SimplifiReportRequest.GeoCity },
  [MapScale.State]: { action: getStateReport, reportRequest: SimplifiReportRequest.GeoState },
};

export const reportDispatch: Record<SimplifiReportView, ActionSet> = {
  [ReportView.Date]: { action: getDateReport, reportRequest: SimplifiReportRequest.Date },
  [ReportView.Channel]: { action: getChannelReport, reportRequest: SimplifiReportRequest.Channel },
  [ReportView.Device]: { action: getDeviceReport, reportRequest: SimplifiReportRequest.Device },
  [ReportView.Advertiser]: {
    action: getAdvertiserReport,
    reportRequest: SimplifiReportRequest.Advertiser,
  },
  [ReportView.Campaign]: {
    action: getCampaignReport,
    reportRequest: SimplifiReportRequest.Campaign,
  },
  [ReportView.Placement]: {
    action: getPlacementReport,
    reportRequest: SimplifiReportRequest.Placement,
  },
  [ReportView.Creative]: {
    action: getCreativeReport,
    reportRequest: SimplifiReportRequest.Creative,
  },
  [ReportView.Publisher]: {
    action: getPublisherReport,
    reportRequest: SimplifiReportRequest.Publisher,
  },
  [ReportView.DaysOfWeek]: {
    action: getDayOfWeekReport,
    reportRequest: SimplifiReportRequest.DayOfWeek,
  },
  [ReportView.Daypart]: { action: getDaypartReport, reportRequest: SimplifiReportRequest.Daypart },
  [ReportView.Deals]: { action: getDealReport, reportRequest: SimplifiReportRequest.Deal },
  [ReportView.GeoFence]: {
    action: getGeoFenceReport,
    reportRequest: SimplifiReportRequest.GeoFence,
  },
  [ReportView.Keyword]: { action: getKeywordReport, reportRequest: SimplifiReportRequest.Keyword },
  [MapScale.Dma]: { action: getDmaReport, reportRequest: SimplifiReportRequest.GeoDma },
  [MapScale.County]: { action: getCountyReport, reportRequest: SimplifiReportRequest.GeoCounty },
  [MapScale.Zip]: { action: getZipReport, reportRequest: SimplifiReportRequest.GeoZip },
  [MapScale.CongressionalDistricts]: {
    action: getCongressionalReport,
    reportRequest: SimplifiReportRequest.GeoCongressionalDistrict,
  },
  [MapScale.City]: { action: getCityReport, reportRequest: SimplifiReportRequest.GeoCity },
  [MapScale.State]: { action: getStateReport, reportRequest: SimplifiReportRequest.GeoState },
  [SIMPLIFI_ZIP4_SCALE]: { action: getZip4Report, reportRequest: SimplifiReportRequest.GeoZip4 },
};
//#endregion

//#region boundaries
export const getZipBoundaries = createAsyncThunk<IGeoJson, { zipCodes: string[] } & IAbortSignalThunkArg>(
  ActionPrefix.ZipBoundaries,
  async payload => {
    const zipCodes = payload?.zipCodes;
    if (!zipCodes?.length) return EMPTY_FEATURE_COLLECTION;

    const responseResult = await axios.get(ZIPCODE_ENDPOINT, {
      params: { zipcode: zipCodes.join(',') },
      headers: borderIOHeaders,
      signal: payload?.signal,
    });

    return responseResult.data;
  },
);

export const getDmaBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.DmaBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardSimplifi: { dmaReport },
    } = getState() as RootState;
    const { signal } = payload || {};
    const requestBody = getBoundariesRequestBody(MAP_SCALES.DMA, dmaReport, BeeswaxReportProperties.DMA_CODE);
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, {
      signal,
    });

    return responseResult.data;
  },
);

export const getStateBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.StateBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardSimplifi: { stateReport },
    } = getState() as RootState;
    const { signal } = payload || {};
    const requestBody = getBoundariesRequestBody(MAP_SCALES.STATE, stateReport, BeeswaxReportProperties.GEO_STATE);
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, {
      signal,
    });

    return responseResult.data;
  },
);

export const getCountyBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.CountyBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardSimplifi: { countyReport },
    } = getState() as RootState;
    const { signal } = payload || {};
    const requestBody = getBoundariesRequestBody(
      MAP_SCALES.COUNTY,
      countyReport,
      BeeswaxReportProperties.GEO_COUNTY_ID,
      250,
    );
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, {
      signal,
    });

    return responseResult.data;
  },
);

export const getCongressionalDistrictBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.CongressionalDistrictBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardSimplifi: { congressionalDistrictReport },
    } = getState() as RootState;
    const { signal } = payload || {};
    const requestBody = getCongressionalBoundariesRequestBody(
      MAP_SCALES.CONGRESSIONAL_DISTRICTS,
      congressionalDistrictReport,
      200,
    );
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, {
      signal,
    });

    return responseResult.data;
  },
);

export const getCityBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.CityBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardSimplifi: { cityReport },
    } = getState() as RootState;
    const { signal } = payload || {};
    // Drop falsy/'-1' codes BEFORE sending: when the per-row report comes from
    // a source that doesn't populate CITY_CODE (e.g. the Simplifi
    // LocationVideoInteraction template returns city names only), the
    // `.map(item => item[CITY_CODE])` call yields a list of `undefined` values
    // that the BE's `required` validator rejects element-by-element with a
    // 100-deep 422 cascade. Filtering here keeps the boundary fetch a no-op
    // when there are no usable codes instead.
    const cityCodes = cityReport
      .map(item => item[BeeswaxReportProperties.CITY_CODE])
      .filter(item => item != null && item !== '-1')
      .slice(0, 100);

    if (!cityCodes.length) return EMPTY_FEATURE_COLLECTION;

    const response = await axios.post(
      GEO_JOINT_BOUNDARIES,
      { data: [{ geoType: MAP_SCALES.CITY, geoIds: cityCodes }] },
      { signal },
    );

    return (
      response.data?.find(({ type }: { type: string }) => type === MAP_SCALES.CITY)?.featureCollection ||
      EMPTY_FEATURE_COLLECTION
    );
  },
);
//#endregion

//#region reducers
export const reportMap: RequestMap<IState> = [
  ['dateReport', getDateReport],
  ['channelReport', getChannelReport],
  ['deviceReport', getDeviceReport],
  ['publisherReport', getPublisherReport],
  ['creativeReport', getCreativeReport],
  ['advertiserReport', getAdvertiserReport],
  ['dayOfWeekReport', getDayOfWeekReport],
  ['daypartReport', getDaypartReport],
  ['placementReport', getPlacementReport],
  ['dealReport', getDealReport],
  ['geoFenceReport', getGeoFenceReport],
  ['geoFenceAttributionReport', getGeoFenceAttributionReport],
  ['keywordReport', getKeywordReport],
  ['stateReport', getStateReport],
  ['dmaReport', getDmaReport],
  ['countyReport', getCountyReport],
  ['zipReport', getZipReport],
  ['zip4Report', getZip4Report],
  ['congressionalDistrictReport', getCongressionalReport],
  ['cityReport', getCityReport],
];

export const boundaryMap: RequestMap<IState> = [
  ['zipBoundariesData', getZipBoundaries],
  ['stateBoundariesData', getStateBoundaries],
  ['countyBoundariesData', getCountyBoundaries],
  ['congressionalDistrictBoundariesData', getCongressionalDistrictBoundaries],
  ['dmaBoundariesData', getDmaBoundaries],
  ['cityBoundariesData', getCityBoundaries],
];
//#endregion
